
import { Folder, ArrowRight } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "@/components/ui/carousel";
import { ProjectTemplate } from "../types";

interface TemplateCarouselProps {
  templates: ProjectTemplate[];
  onUseTemplate: (templateName: string, templateId: number) => void;
}

export const TemplateCarousel = ({
  templates,
  onUseTemplate
}: TemplateCarouselProps) => {
  
  const handleTemplateClick = (template: ProjectTemplate, event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    console.log('Template clicked:', template.title, 'ID:', template.id);
    
    // Ensure the callback is always called with proper parameters
    if (template.id && template.title) {
      onUseTemplate(template.title, template.id);
    } else {
      console.error('Template missing required properties:', template);
    }
  };

  if (!templates || templates.length === 0) {
    return (
      <div className="flex items-center justify-center h-full min-h-[200px] bg-secondary-light/5 border border-primary/20 rounded-lg">
        <div className="text-center text-gray-400">
          <Folder className="h-12 w-12 mx-auto mb-4 opacity-50" />
          <p className="text-lg mb-2">No Project Templates Available</p>
          <p className="text-sm">Please contact your administrator to load project templates.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="relative w-full">
      <Carousel className="w-full">
        <CarouselContent className="px-1 gap-[1px] py-5">
          {templates.map(template => (
            <CarouselItem 
              key={template.id} 
              className="md:basis-1/2 lg:basis-1/2 px-4 sm:px-[70px] flex justify-center"
            >
              <Card 
                className="bg-secondary-light/5 border border-primary/40 transition-all cursor-pointer hover:border-white w-full max-w-[540px] min-h-[200px] sm:h-[155px] sm:w-[540px] flex flex-col"
                onClick={(e) => handleTemplateClick(template, e)}
              >
                <CardHeader className="py-3 flex-shrink-0">
                  <CardTitle className="text-white text-sm sm:text-base">{template.title}</CardTitle>
                </CardHeader>
                <CardContent className="flex-grow flex flex-col justify-between p-4 pt-0">
                  <div className="flex flex-col sm:flex-row sm:items-center text-gray-300 mb-2 text-xs sm:text-sm">
                    <div className="flex items-center mb-1 sm:mb-0">
                      <Folder className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2 text-primary/80" />
                      <span>{template.tasks.length} Tasks</span>
                    </div>
                    <span className="hidden sm:inline mx-2">•</span>
                    <div className="flex items-center mb-1 sm:mb-0">
                      <span>{template.milestones} Milestones</span>
                    </div>
                    <span className="hidden sm:inline mx-2">•</span>
                    <div className="flex items-center">
                      <span>Duration: {template.totalDuration} Days</span>
                    </div>
                  </div>
                  <p className="text-gray-400 text-xs sm:text-sm mb-2 flex-grow">{template.description}</p>
                  <Button 
                    variant="link" 
                    className="text-primary p-0 h-auto self-start pointer-events-none"
                  >
                    Use Template <ArrowRight className="h-4 w-4 sm:h-6 sm:w-6 ml-1" />
                  </Button>
                </CardContent>
              </Card>
            </CarouselItem>
          ))}
        </CarouselContent>
        <div className="flex justify-between w-full absolute top-1/2 -translate-y-1/2 z-10 px-1">
          <CarouselPrevious className="bg-slate-900/80 text-primary hover:bg-slate-800 hover:text-primary/90 left-0" />
          <CarouselNext className="bg-slate-900/80 text-primary hover:bg-slate-800 hover:text-primary/90 right-0" />
        </div>
      </Carousel>
    </div>
  );
};
